import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../redux/store";
import useTabTitle from "../hooks/useTabTitle.hook";
import { getPermissions } from "../redux/slices/permissionsSlice";
import { listUserRoles, archiveUserRole } from "../redux/slices/userRoleSlice";
import { UserRole } from "../interfaces/user-role.interface";
import useDetectSession from "../hooks/useDetectSession.hook";
import {
  pageHeading,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import AppContainer from "../components/AppContainer";
import { PermissionsByDocKey } from "../interfaces/permissions-interface";
import { transformArrToObj } from "../utils/transform-permissions-obj";
import LoadingOverlay from "../components/LoadingOverlay";

const ManageUserRolePage: React.FC = () => {
  useTabTitle();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const {
    userRoles,
    status: userRoleStatus,
    error: userRoleError,
  } = useSelector((state: RootState) => state.userRole);
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  const [currentUserPermissions, setCurrentUserPermissions] =
    useState<PermissionsByDocKey>();

  const [status, setStatus] = useState<string>("loading");
  const [error, setError] = useState<string | null>(null);

  const [showDeletePrompt, setShowDeletePrompt] = useState<boolean>(false);
  const [roleToDelete, setRoleToDelete] = useState<string>("");

  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  useDetectSession();

  useEffect(() => {
    dispatch(getPermissions());
    dispatch(listUserRoles());
  }, [dispatch]);

  useEffect(() => {
    if (currentUser && currentUser.userPermissions) {
      setCurrentUserPermissions(transformArrToObj(currentUser.userPermissions));
    }
  }, [currentUser]);

  useEffect(() => {
    if (userRoleError) {
      setError(userRoleError);
      setSnackbarMessage(userRoleError);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
    if (userRoleStatus) {
      setStatus(userRoleStatus);
    }
  }, [userRoleError, userRoleStatus]);

  const getLastModifiedDate = (epochDate: string) => {
    return new Date(parseFloat(epochDate) * 1000).toLocaleString();
  };

  if (status === "failed") {
    return (
      <AppContainer>
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Box display="flex" alignItems="center" marginBottom={2}>
        <Typography variant="h4" sx={pageHeading}>
          Manage User Roles
        </Typography>
        {currentUserPermissions?.createUserRole?.permissions?.edit && (
          <Button
            style={{ position: "absolute", right: 15 }}
            variant="contained"
            onClick={() => {
              navigate("/create-user-role");
            }}
            sx={primaryBtnStyle}
          >
            Create New User Role
          </Button>
        )}
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <LoadingOverlay loading={status === "loading"} />

      {/* Role List */}
      <Box
        display="flex"
        alignItems="center"
        margin={2}
        flexDirection={"column"}
        style={{ flexGrow: 1 }}
      >
        <TableContainer
          component={Paper}
          sx={{
            overflowX: "initial",
            "& th": {
              backgroundColor: "#E1E1E1",
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Role Name</TableCell>
                <TableCell>Number of Users</TableCell>
                <TableCell>Last Modified Date</TableCell>
                {currentUserPermissions?.userRole?.permissions?.delete && (
                  <TableCell>Delete</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {userRoles?.map((item: UserRole) => (
                <TableRow key={item.roleName}>
                  <TableCell
                    onClick={() => {
                      navigate("/edit-user-role", {
                        state: { roleName: item.roleName },
                      });
                    }}
                  >
                    {item.roleName}
                  </TableCell>
                  <TableCell
                    onClick={() => {
                      navigate("/edit-user-role", {
                        state: { roleName: item.roleName },
                      });
                    }}
                  >
                    {item.numberOfUsers}
                  </TableCell>
                  <TableCell
                    onClick={() => {
                      navigate("/edit-user-role", {
                        state: { roleName: item.roleName },
                      });
                    }}
                  >
                    {getLastModifiedDate(item.lastModifiedDate?._seconds)}
                  </TableCell>
                  {currentUserPermissions?.userRole?.permissions?.delete && (
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setRoleToDelete(item.roleName);
                          setShowDeletePrompt(true);
                        }}
                        disabled={item.numberOfUsers > 0}
                      >
                        <DeleteOutlineIcon
                          color={item.numberOfUsers > 0 ? "disabled" : "error"}
                        />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Dialog
          open={showDeletePrompt}
          onClose={() => {
            setRoleToDelete("");
            setShowDeletePrompt(false);
          }}
          aria-labelledby="Confirm Delete"
        >
          <DialogTitle>Delete {roleToDelete}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this role?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setRoleToDelete("");
                setShowDeletePrompt(false);
              }}
              color="primary"
              sx={[primaryBtnStyleOutline, { width: "80px" }]}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                dispatch(
                  archiveUserRole({
                    data: {
                      roleName: roleToDelete,
                      deletedBy: currentUser?.fullname ?? "",
                    },
                  })
                )
                  .unwrap()
                  .then(() => {
                    setRoleToDelete("");
                    setShowDeletePrompt(false);
                    dispatch(listUserRoles());
                  })
                  .catch(() => {
                    setSnackbarMessage("Failed to delete user role");
                    setSnackbarSeverity("error");
                    setOpenSnackbar(true);
                  });
              }}
              autoFocus
              sx={[primaryBtnStyleOutline, { width: "180px" }]}
            >
              Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AppContainer>
  );
};

export default ManageUserRolePage;
